'use client';

import { c as _c } from "react/compiler-runtime";
import { Spinner } from '@common/components';
import { cn, createOAuthUrl, encodeOAuthState } from '@core/utils';
import { useState } from 'react';
import { LOGIN_TYPE } from '@/api';
import { Image } from '@/components/client';
import { API_ROUTES, ENV, ROUTES, STORAGE_KEY } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useLoginUrlData } from '@/hooks/useLoginUrlData';
import { useUserAgentMobile } from '@/hooks/useUserAgentMobile';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import type { GTM_EVENT_TYPE, GtmEventParams } from '@/utils/analytics/gtm';
type LoginType = 'kakao' | 'naver' | 'google' | 'apple' | 'toss';
interface SocialLoginButtonProps {
  type: LoginType;
  size?: 'large' | 'small';
  eventData?: GtmEventParams[GTM_EVENT_TYPE.SIGN_UP_STEP];
}
export const SocialLoginButton = t0 => {
  const $ = _c(26);
  const {
    type,
    size: t1,
    eventData
  } = t0;
  const size = t1 === undefined ? "large" : t1;
  const {
    saveLoginUrlData
  } = useLoginUrlData();
  const {
    isWelloApp
  } = useCheckWelloApp();
  const isMobile = useUserAgentMobile();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      type: MESSAGE_KEYWORD.LOGIN
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    request: requestSnsLogin,
    isPending: isAppLoginPending
  } = useHandleApp(t2);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isPending = isAppLoginPending || isRedirecting;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      kakao: LOGIN_TYPE.KAKAO,
      naver: LOGIN_TYPE.NAVER,
      google: LOGIN_TYPE.GOOGLE,
      apple: LOGIN_TYPE.APPLE,
      toss: LOGIN_TYPE.TOSS
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const loginType = t3[type];
  let t4;
  if ($[2] !== isMobile || $[3] !== isWelloApp || $[4] !== loginType || $[5] !== requestSnsLogin || $[6] !== type) {
    t4 = () => {
      if (isWelloApp) {
        requestSnsLogin({
          loginType
        });
        return;
      }
      setIsRedirecting(true);
      if (type === "kakao" && isMobile) {
        const redirectUri = `${ENV.NEXT_PUBLIC_DOMAIN}${ROUTES.AUTH.pathname}`;
        const state = encodeOAuthState("kakao", ROUTES.AUTH.pathname);
        if (window.Kakao?.Auth?.authorize) {
          window.Kakao.Auth.authorize({
            redirectUri,
            state
          });
          return;
        }
        console.warn("Kakao SDK not loaded, falling back to REST API");
      }
      location.href = createOAuthUrl({
        type,
        domain: ENV.NEXT_PUBLIC_DOMAIN,
        redirectPath: ROUTES.AUTH.pathname,
        ...{
          kakao: {
            clientId: ENV.NEXT_PUBLIC_KAKAO_CLIENT_ID
          },
          naver: {
            clientId: ENV.NEXT_PUBLIC_NAVER_CLIENT_ID
          },
          google: {
            clientId: ENV.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
            scope: "openid https://www.googleapis.com/auth/userinfo.email"
          },
          apple: {
            clientId: ENV.NEXT_PUBLIC_APPLE_CLIENT_ID,
            scope: "email",
            appleApiRoutePath: API_ROUTES.APPLE_AUTH.pathname
          },
          toss: {
            clientId: ENV.NEXT_PUBLIC_TOSS_CLIENT_ID,
            scope: "user_ci user_birthday user_nationality user_name user_phone user_gender user_email"
          }
        }[type]
      });
    };
    $[2] = isMobile;
    $[3] = isWelloApp;
    $[4] = loginType;
    $[5] = requestSnsLogin;
    $[6] = type;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const moveToOauth = t4;
  let t5;
  if ($[8] !== size || $[9] !== type) {
    t5 = cn("flex h-48 w-full items-center justify-center rounded-small font-600 text-Gray-900 transition-colors typo-body3", {
      kakao: "bg-[#ffde32]",
      naver: "bg-[#20cf5d] text-Gray-white",
      google: "bg-Gray-white border-1 border-Gray-300",
      apple: "bg-Gray-black text-Gray-white",
      toss: "bg-[#0064FF] text-Gray-white"
    }[type], {
      "size-48 flex justify-center items-center !rounded-pill": size === "small"
    });
    $[8] = size;
    $[9] = type;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== eventData || $[12] !== isPending || $[13] !== moveToOauth || $[14] !== saveLoginUrlData) {
    t6 = e => {
      e.preventDefault();
      if (isPending) {
        return;
      }
      if (eventData) {
        sessionStorage.setItem(STORAGE_KEY.IS_LOGIN_PROGRESS, "true");
        sessionStorage.setItem(STORAGE_KEY.SIGNUP_METHOD, eventData.method);
        sessionStorage.setItem(STORAGE_KEY.SIGNUP_TYPE, eventData.signup_type);
      }
      saveLoginUrlData();
      moveToOauth();
    };
    $[11] = eventData;
    $[12] = isPending;
    $[13] = moveToOauth;
    $[14] = saveLoginUrlData;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== isPending || $[17] !== size || $[18] !== type) {
    t7 = isPending ? <Spinner color="currentColor" size={24} /> : <div className={cn("flex w-full max-w-160 items-center justify-start gap-8", {
      "w-fit": size === "small"
    })}><Image unoptimized={true} alt={type} height={24} src={`/assets/pngs/login/${type}.png`} width={24} />{size === "large" ? <span className="whitespace-nowrap">{{
          kakao: "\uCE74\uCE74\uC624",
          naver: "\uB124\uC774\uBC84",
          google: "Google",
          apple: "Apple",
          toss: "\uD1A0\uC2A4"
        }[type]}로 시작하기</span> : null}</div>;
    $[16] = isPending;
    $[17] = size;
    $[18] = type;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== isPending || $[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== type) {
    t8 = <button key={type} className={t5} type="button" disabled={isPending} aria-busy={isPending} onClick={t6}>{t7}</button>;
    $[20] = isPending;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = type;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};